// src/components/ui/modal/ArScanModal.tsx
'use client';

import React, { useMemo, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import {
  Root as ModalRoot,
  Dialog as ModalDialog,
  Content as ModalContent,
  Title as ModalTitle,
} from './Modal';
import { IconPhoto } from '@/components/icons';

export type ArScanModalProps = {
  /** 是否開啟 */
  open: boolean;
  /** 關閉事件 */
  onClose: () => void;
  /** 點擊「開始掃描」 */
  onScan?: () => void;
  /** 標題（預設：掃描線索圖） */
  title?: string;
  /** 內文描述（不傳則顯示預設文案） */
  description?: React.ReactNode;
  /** 顯示大小（沿用 Modal.Dialog 的 size） */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** 是否點擊幕後即可關閉（預設 true） */
  closeOnBackdrop?: boolean;
  /** 是否鎖捲動（預設 true） */
  lockScroll?: boolean;
  /** 自訂 Dialog 額外 class */
  dialogClassName?: string;
  /** 自訂 Content 額外 class */
  contentClassName?: string;

  /** 掃描提示圖（可客製；不傳或空字串即視為「沒有圖片」） */
  scanPic?: string | null;

  /** 沒有圖片時顯示的自訂提示（預設：尚未提供線索圖） */
  emptyHint?: React.ReactNode;

  /** 是否顯示「開啟相機掃線索」按鈕（需搭配 onScan） */
  showScanButton?: boolean;
};

// 你原本的示意預設圖（保留，但不再當作 default）
// 若想永遠有預設圖：呼叫端自行傳入這個常數。
export const DEFAULT_SCAN_PIC =
  'https://womophoto.s3.amazonaws.com/womo/p/store/8BSMemf2OiTfBIk_1760599597.2';

const ArScanModal: React.FC<ArScanModalProps> = ({
  open,
  onClose,
  onScan,
  title = '掃描線索圖',
  description = '掃描線索圖，AR打卡入住AR空間',
  size = 'md',
  closeOnBackdrop = true,
  lockScroll = true,
  dialogClassName,
  contentClassName,
  scanPic, // 不給預設，讓「無圖狀態」能正常出現
  emptyHint,
  showScanButton = false,
}) => {
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgErr, setImgErr] = useState(false);

  // 是否有可用的圖片：字串且非空，且沒發生載入錯誤
  const hasUsablePic = useMemo(() => {
    const ok = typeof scanPic === 'string' && scanPic.trim().length > 0;
    return ok && !imgErr;
  }, [scanPic, imgErr]);

  return (
    <ModalRoot
      open={open}
      onClose={onClose}
      closeOnBackdrop={closeOnBackdrop}
      lockScroll={lockScroll}
      className="modal-static"
    >
      <ModalDialog size={size} centered className={dialogClassName}>
        <ModalContent className={contentClassName}>
          {/* Header */}
          <div className="modal-header">
            <ModalTitle className="fw-bold">{title}</ModalTitle>
            <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
          </div>

          {/* Body */}
          <div className="modal-body">
            <p className="text-center">{description}</p>

            <div className="my-3">
              <div className="position-relative" style={{ aspectRatio: '1 / 1' }}>
                {/* 有圖：Skeleton + 圖片 */}
                {hasUsablePic ? (
                  <>
                    {!imgLoaded && (
                      <div
                        aria-hidden="true"
                        className="scan-skeleton"
                        style={{ borderRadius: 8 }}
                      />
                    )}

                    <Image
                      src={scanPic!}
                      alt="掃描線索圖"
                      fill
                      sizes="100vw"
                      loading="lazy"
                      onLoad={() => setImgLoaded(true)}
                      onError={() => setImgErr(true)}
                      style={{
                        objectFit: 'cover',
                        borderRadius: 8,
                        opacity: imgLoaded ? 1 : 0,
                        transition: 'opacity .3s ease',
                      }}
                    />
                  </>
                ) : (
                  // 無圖：空狀態卡片（不渲染 <Image />，避免空 src 警告）
                  <div
                    className="d-flex flex-column align-items-center justify-content-center text-muted border bg-light"
                    style={{
                      borderStyle: 'dashed',
                      borderRadius: 8,
                      width: '100%',
                      height: '100%',
                      padding: '1rem',
                    }}
                  >
                    <span className="text-grey-700 small">
                      <IconPhoto width={30} />
                    </span>
                    <div className="text-center small">{emptyHint ?? '尚未提供線索圖'}</div>
                    {showScanButton && onScan && (
                      <button
                        type="button"
                        className="btn btn-red mt-3"
                        onClick={onScan}
                        aria-label="開啟相機掃線索"
                      >
                        開啟相機掃線索
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/*下載APP提醒區塊*/}
            <p className="text-center mb-3">請先下載WOMO APP</p>
            <div className="row">
              <div className="col-6 pe-1">
                <Link
                  href="https://apps.apple.com/tw/app/womo-%E7%94%A8%E7%B1%8C%E7%A2%BC%E6%B9%8A%E5%9C%A8%E4%B8%80%E8%B5%B7/id6746141131"
                  target="_blank"
                >
                  <Image
                    src="https://d1q14jmvwk39e0.cloudfront.net/public/assets/images/btn_appstore.png"
                    alt="Download on the App Store"
                    width={400}
                    height={120}
                    style={{ width: '100%', height: 'auto' }}
                  />
                </Link>
              </div>
              <div className="col-6 ps-1">
                <Link
                  href="https://play.google.com/store/apps/details?id=tw.com.womo.womoar&hl=zh_TW"
                  target="_blank"
                >
                  <Image
                    src="https://d1q14jmvwk39e0.cloudfront.net/public/assets/images/btn_googleplay.png"
                    alt="Get it on Google Play"
                    width={400}
                    height={120}
                    style={{ width: '100%', height: 'auto' }}
                  />
                </Link>
              </div>
            </div>

            {/* 可選掃描按鈕（若你想固定顯示也可打開這段） */}
            {/* 
            <div className="d-grid g-2">
              <button type="button" className="btn btn-red mb-3" onClick={onScan}>
                開啟相機掃線索
              </button>
              <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                取消
              </button>
            </div>
            */}
          </div>
        </ModalContent>
      </ModalDialog>

      {/* styled-jsx：灰白色系 Skeleton 動畫 */}
      <style jsx>{`
        .scan-skeleton {
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, #f5f5f5 25%, #e9ecef 37%, #f5f5f5 63%);
          background-size: 400% 100%;
          animation: shimmer 1.4s ease-in-out infinite;
        }
        @keyframes shimmer {
          0% {
            background-position: 100% 0;
          }
          100% {
            background-position: 0 0;
          }
        }
      `}</style>
    </ModalRoot>
  );
};

export default ArScanModal;
